
import { mapActions, mapGetters, mapState } from 'vuex';
import { NodeType } from '@amplify/amp-module';

import { bindAnimationFrame, getThemedCSSVars, isObjectEmpty } from '../../utils';
import propsForMixin from '../../mixins/props-for';
import components from '../node-components';
import Edetailer from './Edetailer';
import LoadingIndicator from './LoadingIndicator';
import Node from './Node';
import Nudge from './Nudge';
import PasswordProtect from './PasswordProtect';

export default {
  components: {
    ...components,
    Edetailer,
    LoadingIndicator,
    Node,
    Nudge,
    PasswordProtect,
  },

  mixins: [propsForMixin],

  props: {
    previewAmp: {
      type: Object,
      default : null
    },
    previewStack: {
      type: Array,
      default: () => null,
    },
    handleHighlightChange: {
      type: Function,
      default: () => {}
    },
    showEdetail: {
      type: Boolean,
      default: false,
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    canRunPreview: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      menuOpen: false,
    }
  },

  computed: {
    ...mapState('chatbotStore', [
      'isAmpLoading',
      'isSlideLoading',
      'showNudge',
      'showPasswordPage',
      'crmConnector',
      'crmLoading',
    ]),

    ...mapState('chatbotPluginStore', [
      'isLoading'
    ]),

    ...mapGetters('chatbotStore', [
      'ampConfig',
      'ampHasMenu',
      'currentAmp',
      'currentAmpId',
      'currentStack',
      'getLatestConversationId',
      'getTriggeredConversation',
      'isPreview',
      'showStackedEdetail',
    ]),

    pluginIsLoading() {
      return this.$store?.hasModule?.('chatbotPluginStore') && this.isLoading;
    },

    conversationStack() {
      if (this.isPreview) {
        return this.canRunPreview
          ? (this.currentStack?.length ? this.currentStack : this.previewStack) || []
          : this.previewStack || [];
      }

      return this.currentStack || [];
    },

    ampName() {
      return this.ampConfig.name || '';
    },

    firstLetter() {
      return this.ampName?.[0];
    },

    cssVars() {
      return getThemedCSSVars(this.ampConfig);
    },

    ampMenu() {
      return this.currentAmp?.data?.menu || {};
    },

    highlightedNode() {
      const lastStackItem = this.currentStack?.findLast((item) => !item.delayed);

      return lastStackItem ? lastStackItem.node : null;
    },

    highlightedNodeId() {
      return this.highlightedNode ? this.highlightedNode.id : null;
    },

    isEdetailSlideLoading() {
      return this.isSlideLoading && this.currentAmp.ampType === 'edetailer' && !this.isPreview;
    },

    contentRefShown() {
      return !this.showPasswordPage && !this.isAmpLoading && this.currentAmpId;
    },

    showLoadingMessage() {
      return this.pluginIsLoading || this.isEdetailSlideLoading;
    },

    loadingCrmAccountInfo() {
      return !!this.crmConnector && this.crmLoading;
    },

    showContent() {
      return this.currentAmpId || !isObjectEmpty(this.previewAmp);
    },
  },

  watch: {
    previewAmp: {
      handler(amp){
        this.setPreviewAmp(amp);
      },
      immediate: true,
    },

    currentAmp(amp) {
      if (amp && !isObjectEmpty(amp) && !this.isPreview) {
        this.setupAmp(amp);
      }
    },

    currentStack: {
      handler(newStack) {
        this.$nextTick(this.scrollToBottom);
        const lastStackItem = newStack?.findLast((item) => !item.delayed);

        if (lastStackItem) {
          this.setHighlightedNode(lastStackItem.node);
        }
      },
      deep: true,
    },

    highlightedNode(newNode, oldNode) {
      if (newNode && newNode.id !== oldNode?.id) {
        this.handleHighlightChange(newNode);
      }
    },

    showNudge(value) {
      if (value) {
        this.$nextTick(this.scrollToBottom);
      }
    },

    contentRefShown(shown, prevShown) {
      if (shown && !prevShown) {
        this.$nextTick(this.scrollToBottom);
      }
    },
  },

  async created() {
    if (!this.isPreview) {
      await this.$chatbotService.fetchInitialAmp();
    }
  },

  mounted() {
    this.scrollToBottom = bindAnimationFrame(this.handleScrollToBottom);
    this.$nextTick(this.scrollToBottom);
  },

  methods: {
    ...mapActions('chatbotStore', ['setOpenState', 'setHighlightedNode', 'popAmp', 'setPreviewAmp']),

    setupAmp(amp) {
      this.$chatbotService.setupAmp(amp);
    },

    handleScrollToBottom() {
      if (this.$refs.content) {
        this.$refs.content.scrollTop = this.$refs.content.scrollHeight;
      }
    },

    onClose() {
      if (this.isPreview) {
        return;
      }

      if (this.showStackedEdetail) {
        this.popAmp();

        this.$nextTick(() => {
          const lastOpenNode = this.currentStack.findLast((item) => item.node.type === NodeType.open);

          this.$tracking?.sendResponseEvent('selfDetailerChange', {
            conversationId: this.getLatestConversationId,
            openId: lastOpenNode?.node?.id,
            changeEvent: 'CLOSE',
          });
        });

        return;
      }

      this.setOpenState(false);
    },

    toggleMenu(state) {
      this.menuOpen = state !== undefined ? state : !this.menuOpen;
    },

    openAmplify() {
      window.open('https://amplify.anthill.technology/', '_blank');
    },

    shouldRenderNode(node) {
      if (this.isPreview && node.type === NodeType.goTo && node.webLink) {
        return true;
      }

      const excludedNodes = this.isPreview
        ? [NodeType.goTo, NodeType.condition]
        : [NodeType.goTo, NodeType.condition, NodeType.open];

      return !excludedNodes.includes(node.type);
    },

    onMenuClick() {
      if (this.isPreview) {
        return
      }

      this.toggleMenu(true);
    },

    getNodeComponent(nodeType) {
      return nodeType === 'form' ? 'formNode' : nodeType;
    },
  },
};
